import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useMemo, useCallback, useRef, useEffect } from 'react';
import { twJoin } from 'tailwind-merge';

interface Props {
    currentYear: Date;
    selectedDate: Nullable<Date>;
    yearRange: number;
    minDate?: Date;
    maxDate?: Date;
    onYearClick: (year: number, isDisabled: boolean) => void;
    focusedYearIndex: number;
    onFocusedYearChange?: (index: number) => void;
}

export function YearsView({
    currentYear,
    selectedDate,
    yearRange,
    minDate,
    maxDate,
    onYearClick,
    focusedYearIndex,
    onFocusedYearChange,
}: Props) {
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const years = useMemo(() => {
        const currentYears = currentYear.getFullYear();
        const startYear = currentYears;
        const today = new Date();

        return Array.from({ length: yearRange + 1 }, (_, i) => {
            const year = startYear + i;
            let isDisabled = false;
            if (minDate && new Date(year + 1, 0, 0) < minDate) isDisabled = true;
            if (maxDate && new Date(year, 0, 1) > maxDate) isDisabled = true;

            return {
                year,
                isCurrentYear: year === currentYears,
                isSelected: !!(selectedDate && year === selectedDate.getFullYear()),
                isToday: year === today.getFullYear(),
                isDisabled,
            };
        });
    }, [currentYear, yearRange, minDate, maxDate, selectedDate]);

    const handleYearClick = useCallback(
        (yearData: (typeof years)[0], index: number) => {
            onYearClick(yearData.year, yearData.isDisabled);
            onFocusedYearChange?.(index);
        },
        [onYearClick, onFocusedYearChange],
    );

    const focusYear = useCallback((index: number) => {
        const button = buttonRefs.current[index];
        if (button && !button.disabled) {
            button.focus();
            return;
        }
        buttonRefs.current.find(button => button != null && !button.disabled)?.focus();
    }, []);

    useEffect(() => {
        if (focusedYearIndex >= 0 && focusedYearIndex < buttonRefs.current.length) {
            focusYear(focusedYearIndex);
        }
    }, [focusedYearIndex, focusYear]);

    useChangeEffect(() => {
        const id = requestAnimationFrame(() => focusYear(focusedYearIndex));
        return () => cancelAnimationFrame(id);
    }, focusYear);

    return (
        <div className="grid grid-cols-4 gap-2" role="grid">
            {years.map((yearData, index) => (
                <button
                    key={index}
                    ref={el => {
                        buttonRefs.current[index] = el;
                    }}
                    type="button"
                    role="gridcell"
                    tabIndex={index === focusedYearIndex ? 0 : -1}
                    className={twJoin(
                        'rounded py-3 text-md md:py-2 md:text-sm',
                        !yearData.isDisabled && !yearData.isSelected && 'hover:bg-neutral-100',
                        yearData.isToday && !yearData.isSelected && 'border border-neutral-400',
                        yearData.isSelected && 'bg-primary-500 text-white',
                        yearData.isDisabled && 'cursor-not-allowed opacity-50',
                    )}
                    onClick={() => handleYearClick(yearData, index)}
                    disabled={yearData.isDisabled}
                    aria-label={yearData.year.toString()}
                    aria-selected={yearData.isSelected || undefined}>
                    {yearData.year}
                </button>
            ))}
        </div>
    );
}
